import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { usePlayerContext } from "../providers/PlayerProvider";
import { t } from "../i18n.js";

function formatTime(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function formatFileSize(bytes) {
  if (!bytes) return "未知";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(2) + " MB";
}

const CATEGORY_COVERS = [
  "https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1518998053901-5348d3961a04?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1443884200821-1a07e74d1a0d?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=400&fit=crop",
];

function getCoverUrl(index) {
  return CATEGORY_COVERS[index % CATEGORY_COVERS.length];
}

function sourceLabel(src, name, lang) {
  switch (src) {
    case "explore":
      return t(lang, "exploreSource");
    case "liked":
      return t(lang, "likedSource");
    case "favorites":
      return t(lang, "favoritesSource");
    case "playlist":
      return name || t(lang, "playlistSource");
    case "temp":
      return t(lang, "tempSource");
    default:
      return src;
  }
}

const getLoopLabel = (lang, mode) => {
  const labels = {
    "repeat-all": { icon: "🔁", label: t(lang, "loopAll") },
    "repeat-one": { icon: "🔂", label: t(lang, "loopOne") },
    shuffle: { icon: "🔀", label: t(lang, "shuffleMode") },
    none: { icon: "➡️", label: t(lang, "loopOff") },
  };
  return labels[mode] || labels["repeat-all"];
};

/** ── 实时波形 Canvas ── */
function Waveform({ audioRef, currentTrackId }) {
  const canvasRef = useRef(null);
  const peaksRef = useRef(null); // { peaks: {min,max}[], audioUrl: string }

  // 解码音频 → 提取波形峰值（每桶 min/max 对，用于双极波形曲线）
  const decodeWaveform = useCallback(async (audioUrl) => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const response = await fetch(audioUrl);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
      audioCtx.close();

      const rawData = audioBuffer.getChannelData(0);
      const numPeaks = 300; // 曲线点数（更高分辨率让曲线更平滑）
      const blockSize = Math.floor(rawData.length / numPeaks);
      const peaks = []; // { min: -1~0, max: 0~1 }
      for (let i = 0; i < numPeaks; i++) {
        let mn = 0, mx = 0;
        for (let j = 0; j < blockSize; j++) {
          const s = rawData[i * blockSize + j] || 0;
          if (s < mn) mn = s;
          if (s > mx) mx = s;
        }
        peaks.push({ min: mn, max: mx });
      }
      // 归一化 min/max 到 -1~1
      let maxAbs = 0.001;
      for (const p of peaks) maxAbs = Math.max(maxAbs, -p.min, p.max);
      return peaks.map((p) => ({ min: p.min / maxAbs, max: p.max / maxAbs }));
    } catch (e) {
      console.error("[Waveform] decode error:", e);
      return null;
    }
  }, []);

  // currentTrackId 变化时，解码并缓存
  useEffect(() => {
    if (!currentTrackId || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // 渲染静态波形曲线（连续平滑曲线，非柱状）
    const render = (peaks) => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      const w = rect.width;
      const h = rect.height;
      const mid = h / 2;
      const amp = h * 0.42; // 振幅缩放

      ctx.clearRect(0, 0, w, h);

      // 背景
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, "rgba(99,102,241,0.08)");
      grad.addColorStop(0.5, "rgba(99,102,241,0.04)");
      grad.addColorStop(1, "rgba(99,102,241,0.08)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      if (!peaks) return;

      const step = w / peaks.length;

      // —— 填充波形（从曲线到中线的渐变） ——
      ctx.beginPath();
      ctx.moveTo(0, mid - peaks[0].max * amp);
      for (let i = 1; i < peaks.length; i++) {
        const x = i * step;
        const y = mid - peaks[i].max * amp;
        ctx.lineTo(x, y);
      }
      // 下半部分，从右到左画 min 曲线
      for (let i = peaks.length - 1; i >= 0; i--) {
        const x = i * step;
        const y = mid - peaks[i].min * amp;
        ctx.lineTo(x, y);
      }
      ctx.closePath();
      // 波形填充（紫色渐变）
      const fillGrad = ctx.createLinearGradient(0, mid - amp, 0, mid + amp);
      fillGrad.addColorStop(0, "rgba(139,92,246,0.35)");
      fillGrad.addColorStop(0.5, "rgba(139,92,246,0.20)");
      fillGrad.addColorStop(1, "rgba(139,92,246,0.35)");
      ctx.fillStyle = fillGrad;
      ctx.fill();

      // —— 波形轮廓线 ——
      ctx.strokeStyle = "rgba(139,92,246,0.5)";
      ctx.lineWidth = 1.5;
      // 上轮廓
      ctx.beginPath();
      ctx.moveTo(0, mid - peaks[0].max * amp);
      for (let i = 1; i < peaks.length; i++) {
        ctx.lineTo(i * step, mid - peaks[i].max * amp);
      }
      ctx.stroke();
      // 下轮廓
      ctx.beginPath();
      ctx.moveTo(0, mid - peaks[0].min * amp);
      for (let i = 1; i < peaks.length; i++) {
        ctx.lineTo(i * step, mid - peaks[i].min * amp);
      }
      ctx.stroke();

      // 中心线（细、低调）
      ctx.strokeStyle = "rgba(139,92,246,0.25)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 6]);
      ctx.beginPath();
      ctx.moveTo(0, mid);
      ctx.lineTo(w, mid);
      ctx.stroke();
      ctx.setLineDash([]);
    };

    // 已缓存则直接渲染
    if (peaksRef.current && peaksRef.current.audioUrl === audioRef?.current?.src) {
      render(peaksRef.current.peaks);
      return;
    }

    // 获取当前音频 URL（从 audioRef 或 track API）
    let audioUrl = audioRef?.current?.src;
    if (!audioUrl || audioUrl === window.location.href) {
      // audioRef 尚未设置，从 API 获取
      render(null);
      return;
    }

    decodeWaveform(audioUrl).then((peaks) => {
      if (!peaks) return;
      peaksRef.current = { peaks, audioUrl };
      render(peaks);
    });
  }, [currentTrackId, audioRef, decodeWaveform]);

  // 播放时间更新时，叠加播放进度指示器
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio || !canvasRef.current) return;

    const updateProgress = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;

      // 重绘波形（全部，但已播部分更亮）
      const peaks = peaksRef.current?.peaks;
      if (!peaks) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const mid = h / 2;
      const amp = h * 0.42;

      // 背景
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, "rgba(99,102,241,0.08)");
      grad.addColorStop(0.5, "rgba(99,102,241,0.04)");
      grad.addColorStop(1, "rgba(99,102,241,0.08)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      const step = w / peaks.length;
      const progress = audio.duration > 0 ? audio.currentTime / audio.duration : 0;
      const splitIdx = Math.floor(progress * peaks.length);

      // —— 已播部分（亮紫色填充 + 轮廓） ——
      if (splitIdx > 0) {
        ctx.beginPath();
        ctx.moveTo(0, mid - peaks[0].max * amp);
        for (let i = 1; i < splitIdx; i++) {
          ctx.lineTo(i * step, mid - peaks[i].max * amp);
        }
        ctx.lineTo(splitIdx * step, mid - peaks[splitIdx - 1].max * amp);
        for (let i = splitIdx - 1; i >= 0; i--) {
          ctx.lineTo(i * step, mid - peaks[i].min * amp);
        }
        ctx.closePath();
        const pg = ctx.createLinearGradient(0, mid - amp, 0, mid + amp);
        pg.addColorStop(0, "rgba(139,92,246,0.7)");
        pg.addColorStop(0.5, "rgba(139,92,246,0.5)");
        pg.addColorStop(1, "rgba(139,92,246,0.7)");
        ctx.fillStyle = pg;
        ctx.fill();
      }

      // —— 全部波形轮廓线（已播亮、未播淡） ——
      ctx.lineWidth = 1.5;
      // 上轮廓
      for (let i = 0; i < peaks.length; i++) {
        if (i < splitIdx) {
          ctx.strokeStyle = "rgba(167,117,250,0.8)";
        } else {
          ctx.strokeStyle = "rgba(139,92,246,0.25)";
        }
        ctx.beginPath();
        ctx.moveTo(i * step, mid - peaks[i].max * amp);
        ctx.lineTo(i * step + step, mid - peaks[Math.min(i + 1, peaks.length - 1)].max * amp);
        ctx.stroke();
      }
      // 下轮廓
      for (let i = 0; i < peaks.length; i++) {
        if (i < splitIdx) {
          ctx.strokeStyle = "rgba(167,117,250,0.8)";
        } else {
          ctx.strokeStyle = "rgba(139,92,246,0.25)";
        }
        ctx.beginPath();
        ctx.moveTo(i * step, mid - peaks[i].min * amp);
        ctx.lineTo(i * step + step, mid - peaks[Math.min(i + 1, peaks.length - 1)].min * amp);
        ctx.stroke();
      }

      // 播放位置指示线
      if (progress > 0 && progress < 1) {
        ctx.strokeStyle = "rgba(255,255,255,0.5)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(progress * w, 0);
        ctx.lineTo(progress * w, h);
        ctx.stroke();
      }

      // 中心虚线
      ctx.strokeStyle = "rgba(139,92,246,0.2)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 6]);
      ctx.beginPath();
      ctx.moveTo(0, mid);
      ctx.lineTo(w, mid);
      ctx.stroke();
      ctx.setLineDash([]);
    };

    audio.addEventListener("timeupdate", updateProgress);
    updateProgress();
    return () => audio.removeEventListener("timeupdate", updateProgress);
  }, [audioRef, currentTrackId]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-20 rounded-xl"
      style={{ background: "rgba(255,255,255,0.03)" }}
    />
  );
}

/** ── 播放来源选择器 ── */
function SourcePicker({ currentSource, playlists, onSwitch, lang }) {
  const items = [
    { key: "explore", label: "🎯 " + t(lang, "exploreSource") },
    { key: "liked", label: "❤️ " + t(lang, "likedSource") },
    { key: "favorites", label: "⭐ " + t(lang, "favoritesSource") },
    { key: "temp", label: "📋 " + t(lang, "tempSource") },
    ...(playlists || []).map((p) => ({
      key: `playlist:${p.id}`,
      label: `📋 ${p.name}`,
      origKey: "playlist",
      playlistId: p.id,
    })),
  ];

  return (
    <div className="space-y-0.5">
      {items.map((item) => (
        <button
          key={item.key}
          onClick={() =>
            onSwitch(
              item.origKey === "playlist" ? "playlist" : item.key,
              item.origKey === "playlist" ? item.playlistId : undefined
            )
          }
          className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition ${
            (currentSource === item.key && !item.origKey) ||
            (item.origKey === "playlist" && currentSource === "playlist")
              ? "bg-indigo-500/20 text-indigo-300"
              : "text-white/50 hover:bg-white/5 hover:text-white/80"
          }`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

/** ── 主组件 ── */
export default function FullscreenPlayer({
  likedTracks,
  favoritedTracks,
  onToggleLike,
  onToggleFavorite,
  onAddToPlaylist,
  onTagClick,
  playlists,
  lang = "zh",
}) {
  const {
    audioRef,
    currentTrack,
    currentScene,
    isPlaying,
    setPlayerMode,
    togglePlay,
    prevTrack,
    nextTrack,
    playlist,
    playlistSource,
    setPlaylistSource,
    setPlaylist,
    loopMode,
    setLoopMode,
    playTrack,
    tempPlaylist,
    exploreTracks,
    handleShare,
  } = usePlayerContext();

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [showSourcePicker, setShowSourcePicker] = useState(false);
  const [playlistName, setPlaylistName] = useState("");
  const [showDetails, setShowDetails] = useState(true);
  const [showMobilePlaylist, setShowMobilePlaylist] = useState(false);
  const progressRef = useRef(null);
  const volumeRef = useRef(null);
  const audio = audioRef?.current;

  const isLiked = likedTracks?.has(currentTrack?.id);
  const isFavorited = favoritedTracks?.has(currentTrack?.id);

  const [coverIndex] = useState(() => Math.floor(Math.random() * CATEGORY_COVERS.length));

  // ── 从共享 audio 同步时间 ──
  useEffect(() => {
    if (!audio) return;
    const update = () => {
      if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration);
      setCurrentTime(audio.currentTime);
    };
    const onMeta = () => {
      if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration);
    };
    audio.addEventListener("timeupdate", update);
    audio.addEventListener("loadedmetadata", onMeta);
    update();
    return () => {
      audio.removeEventListener("timeupdate", update);
      audio.removeEventListener("loadedmetadata", onMeta);
    };
  }, [audio, currentTrack?.id]);

  // ── 音量 ──
  useEffect(() => {
    if (audio) audio.volume = volume;
  }, [volume, audio]);

  // ── 进度跳转 ──
  const handleSeek = useCallback(
    (e) => {
      if (!progressRef.current || !audio) return;
      const rect = progressRef.current.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      if (duration > 0) audio.currentTime = ratio * duration;
    },
    [audio, duration]
  );

  // ── 音量跳转 ──
  const handleVolumeSeek = useCallback(
    (e) => {
      if (!volumeRef.current) return;
      const rect = volumeRef.current.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      setVolume(ratio);
    },
    []
  );

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // ── 智能播放：无曲目时自动加载第一首 ──
  const handlePlayPause = useCallback(() => {
    if (currentTrack) {
      togglePlay();
    } else if (playlist && playlist.length > 0) {
      playTrack(playlist[0], currentScene || null);
    } else {
      // 没有 playlist 也没有曲目时，静默尝试 explore
      togglePlay();
    }
  }, [currentTrack, togglePlay, playlist, playTrack, currentScene]);

  // ── 下载 ──
  const handleDownload = useCallback(async () => {
    if (!currentTrack) return;
    const name = currentTrack.name_zh || currentTrack.name_en || currentTrack.name || "track";
    let downloadUrl = currentTrack.url; // default

    const token = localStorage.getItem("fm_token");
    if (token) {
      try {
        const res = await fetch(`/api/tracks/${currentTrack.id}/download`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.url) downloadUrl = data.url;
        }
      } catch {
        // fallback to currentTrack.url
      }
    }

    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `${name}.mp3`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [currentTrack]);

  // ── 循环模式切换 ──
  const cycleLoopMode = () => {
    const order = ["repeat-all", "repeat-one", "shuffle", "none"];
    const idx = order.indexOf(loopMode);
    setLoopMode(order[(idx + 1) % order.length]);
  };

  // ── 切换播放列表来源 ──
  const switchSource = (src, playlistId) => {
    if (playlistId) {
      const token = localStorage.getItem("fm_token");
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      // 查找歌单名称
      const pl = (playlists || []).find((p) => p.id === playlistId);
      if (pl) setPlaylistName(pl.name);
      fetch(`/api/playlists/${playlistId}`, { headers })
        .then((r) => {
          if (!r.ok) throw new Error("HTTP " + r.status);
          return r.json();
        })
        .then((data) => {
          const items = data.tracks || [];
          const tracks = items.map((item) => item.track).filter(Boolean);
          setPlaylist(tracks);
          setPlaylistSource("playlist");
          if (tracks.length > 0) {
            playTrack(tracks[0], currentScene || null);
          }
        })
        .catch(() => {});
    } else {
      setPlaylistSource(src);
      setPlaylistName(""); // 切换到非歌单源时清空名称
    }
    setShowSourcePicker(false);
  };

  // ── 获取当前源曲目列表（用于左侧显示） ──
  const sourceTracks =
    playlistSource === "liked"
      ? Array.from((likedTracks || new Map()).values())
          .filter((item) => item.track && item.track.id)
          .map((item) => item.track)
      : playlistSource === "favorites"
        ? Array.from((favoritedTracks || new Map()).values())
            .filter((item) => item.track && item.track.id)
            .map((item) => item.track)
        : playlistSource === "temp"
          ? tempPlaylist
          : playlistSource === "explore"
            ? exploreTracks
            : playlist;

  const bgImage = currentScene?.image || getCoverUrl(coverIndex);
  const loopInfo = getLoopLabel(lang, loopMode);

  const track = currentTrack;
  const scene = currentScene;

  return (
    <div className="fixed inset-0 z-50 flex animate-fade-in overflow-hidden">
      {/* 背景 - 毛玻璃 + 场景图 */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${bgImage})` }}
      />
      <div className="absolute inset-0 bg-black/70 backdrop-blur-xl" />

      {/* 实际内容 */}
      <div className="relative z-10 flex w-full max-w-[1400px] mx-auto h-full">
        {/* ════ 左侧播放列表 ════ */}
        <div className="hidden lg:flex w-72 flex-col border-r border-white/10 shrink-0 bg-black/40">
          {/* 头部 */}
          <div className="px-4 py-4 border-b border-white/10 shrink-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-white text-sm font-semibold">{t(lang, "playlist")}</h3>
              <span className="text-xs text-white/40">{sourceTracks.length} {t(lang, "tracks")}</span>
            </div>
            {/* 来源选择器 */}
            <button
              onClick={() => setShowSourcePicker(!showSourcePicker)}
              className="flex items-center gap-1 text-xs text-white/40 hover:text-white/70"
            >
              {sourceLabel(playlistSource, playlistName, lang)}
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={`transition-transform ${showSourcePicker ? "rotate-180" : ""}`}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {showSourcePicker && (
              <div className="mt-2 pt-2 border-t border-white/5">
                <SourcePicker
                  show={showSourcePicker}
                  setShow={setShowSourcePicker}
                  currentSource={playlistSource}
                  playlists={playlists}
                  onSwitch={switchSource}
                  lang={lang}
                />
              </div>
            )}
          </div>

          {/* 曲目列表 */}
          <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
            {sourceTracks.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-white/20 text-xs">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-2 opacity-30">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
                {t(lang, "playlistEmpty")}
              </div>
            ) : (
              sourceTracks.map((t, idx) => {
                const isCurrent = t.id === track?.id;
                return (
                  <button
                    key={t.id || idx}
                    onClick={() => {
                      const sc = scene;
                      playTrack(t, sc);
                    }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition ${
                      isCurrent
                        ? "bg-indigo-500/15 text-indigo-300"
                        : "text-white/60 hover:bg-white/5 hover:text-white/80"
                    }`}
                  >
                    <span className="text-xs font-mono w-4 text-right shrink-0 text-white/20">
                      {isCurrent ? "▶" : idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate ${isCurrent ? "font-medium" : ""}`}>
                        {t.name_zh || t.name_en || t.name || "未知曲目"}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ════ 移动端播放列表覆盖层 ════ */}
        {showMobilePlaylist && (
          <div className="fixed inset-0 z-50 lg:hidden flex">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowMobilePlaylist(false)}
            />
            <div className="relative w-72 max-w-[85vw] h-full bg-gray-900/95 backdrop-blur-xl shadow-2xl flex flex-col animate-slide-in-right">
              {/* 头部 */}
              <div className="px-4 py-4 border-b border-white/10 shrink-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-white text-sm font-semibold">{t(lang, "playlist")}</h3>
                  <span className="text-xs text-white/40">{sourceTracks.length} {t(lang, "tracks")}</span>
                </div>
                <button
                  onClick={() => setShowSourcePicker(!showSourcePicker)}
                  className="flex items-center gap-1 text-xs text-white/40 hover:text-white/70"
                >
                  {sourceLabel(playlistSource, playlistName, lang)}
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`transition-transform ${showSourcePicker ? "rotate-180" : ""}`}>
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                {showSourcePicker && (
                  <div className="mt-2 pt-2 border-t border-white/5">
                    <SourcePicker
                      show={showSourcePicker}
                      setShow={setShowSourcePicker}
                      currentSource={playlistSource}
                      playlists={playlists}
                      onSwitch={switchSource}
                      lang={lang}
                    />
                  </div>
                )}
              </div>
              {/* 曲目列表 */}
              <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
                {sourceTracks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-white/20 text-xs">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-2 opacity-30">
                      <path d="M9 18V5l12-2v13" />
                      <circle cx="6" cy="18" r="3" />
                      <circle cx="18" cy="16" r="3" />
                    </svg>
                    {t(lang, "playlistEmpty")}
                  </div>
                ) : (
                  sourceTracks.map((t, idx) => {
                    const isCurrent = t.id === track?.id;
                    return (
                      <button
                        key={t.id || idx}
                        onClick={() => {
                          const sc = scene;
                          playTrack(t, sc);
                          setShowMobilePlaylist(false);
                        }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition ${
                          isCurrent
                            ? "bg-indigo-500/15 text-indigo-300"
                            : "text-white/60 hover:bg-white/5 hover:text-white/80"
                        }`}
                      >
                        <span className="text-xs font-mono w-4 text-right shrink-0 text-white/20">
                          {isCurrent ? "▶" : idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm truncate ${isCurrent ? "font-medium" : ""}`}>
                            {t.name_zh || t.name_en || t.name || "未知曲目"}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {/* ════ 右侧播放器 ════ */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-black/30">
          {/* ── 顶部栏 ── */}
          <div className="flex items-center justify-between px-6 py-3 shrink-0 border-b border-white/5">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPlayerMode("bar")}
                className="text-white/40 hover:text-white transition flex items-center gap-1.5 text-xs"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                {t(lang, "back")}
              </button>
              {/* 移动端播放列表切换按钮 */}
              <button
                onClick={() => setShowMobilePlaylist(!showMobilePlaylist)}
                className="lg:hidden w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5 transition"
                title={t(lang, "playlist")}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPlayerMode("immersive")}
                className="text-xs text-white/40 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-full transition"
              >
                {t(lang, "immersionPlay")}
              </button>
              <button
                onClick={() => setPlayerMode("bar")}
                className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                </svg>
              </button>
            </div>
          </div>

          {/* ── 可滚动内容 ── */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* 封面 */}
              <div className="shrink-0 mx-auto lg:mx-0">
                <div
                  className="w-28 h-28 sm:w-36 sm:h-36 lg:w-44 lg:h-44 rounded-2xl bg-cover bg-center shadow-2xl shadow-black/50"
                  style={{ backgroundImage: `url(${bgImage})` }}
                />
              </div>

              {/* 右侧信息 */}
              <div className="flex-1 min-w-0">
                {/* 标题 */}
                <Link
                  to={track ? `/track/${track.id}` : '#'}
                  className="text-2xl font-bold text-white leading-tight hover:text-indigo-300 transition"
                  onClick={(e) => e.stopPropagation()}
                >
                  {track?.name_zh || track?.name_en || track?.name || "未知曲目"}
                </Link>
                <p className="text-sm text-white/40 mt-1">
                  {scene?.emoji} {scene?.name || scene?.nameEn || "场景音乐"}
                </p>

                {/* 波形 */}
                <div className="mt-4">
                  <Waveform audioRef={audioRef} currentTrackId={track?.id} />
                </div>

                {/* 进度条 */}
                <div className="mt-4">
                  <div
                    ref={progressRef}
                    onClick={handleSeek}
                    className="relative h-1.5 bg-white/10 rounded-full cursor-pointer group"
                  >
                    <div
                      className="absolute left-0 top-0 h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ left: `calc(${progress}% - 7px)` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-white/30 font-mono mt-1.5">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>

                {/* 控制按钮 */}
                <div className="flex items-center justify-between mt-5">
                  <div className="flex items-center gap-3">
                    <button onClick={prevTrack} className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center transition">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 6h2v12H6V6zm3.5 6 8.5 6V6l-8.5 6z" />
                      </svg>
                    </button>
                    <button
                      onClick={handlePlayPause}
                      className="w-14 h-14 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white flex items-center justify-center transition shadow-lg shadow-indigo-500/20 hover:scale-105 active:scale-95"
                    >
                      {isPlaying ? (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                          <rect x="6" y="4" width="4" height="16" rx="1" />
                          <rect x="14" y="4" width="4" height="16" rx="1" />
                        </svg>
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="ml-1">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      )}
                    </button>
                    <button onClick={nextTrack} className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center transition">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
                      </svg>
                    </button>
                  </div>

                  {/* 动作按钮 */}
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => onToggleLike?.(track?.id, track, scene)}
                      className={`w-9 h-9 rounded-full flex items-center justify-center transition ${
                        isLiked ? "text-red-400 bg-red-500/10" : "text-white/40 hover:text-red-400 hover:bg-red-500/5"
                      }`}
                      title={t(lang, "like")}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => onToggleFavorite?.(track?.id, track, scene)}
                      className={`w-9 h-9 rounded-full flex items-center justify-center transition ${
                        isFavorited ? "text-amber-400 bg-amber-500/10" : "text-white/40 hover:text-amber-400 hover:bg-amber-500/5"
                      }`}
                      title={t(lang, "favorite")}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill={isFavorited ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleShare(track)}
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5 transition"
                      title={t(lang, "share")}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                      </svg>
                    </button>
                    <button
                      onClick={() => onAddToPlaylist?.(track)}
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5 transition"
                      title={t(lang, "addToPlaylist")}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                    <button
                      onClick={handleDownload}
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5 transition"
                      title={t(lang, "download")}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* 第二行：循环模式 + 音量 */}
                <div className="flex items-center gap-6 mt-5">
                  <button
                    onClick={cycleLoopMode}
                    className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition"
                    title={loopInfo.label}
                  >
                    <span>{loopInfo.icon}</span>
                    <span>{loopInfo.label}</span>
                  </button>

                  <div className="flex items-center gap-2 flex-1 max-w-[200px]">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white/30 shrink-0">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                    </svg>
                    <div
                      ref={volumeRef}
                      onClick={handleVolumeSeek}
                      className="flex-1 h-1 bg-white/10 rounded-full cursor-pointer relative group"
                    >
                      <div
                        className="absolute left-0 top-0 h-full bg-white/40 rounded-full transition-all"
                        style={{ width: `${volume * 100}%` }}
                      />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ left: `calc(${volume * 100}% - 6px)` }}
                      />
                    </div>
                  </div>
                </div>

                {/* 标签 */}
                {scene?.tags && scene.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-5">
                    {scene.tags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => onTagClick?.(tag)}
                        className="px-2.5 py-1 bg-white/5 hover:bg-indigo-500/15 hover:text-indigo-300 text-white/40 text-xs rounded-full transition"
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── 分隔线 ── */}
            <div className="my-6 border-t border-white/5" />

            {/* ── 曲目详细信息 ── */}
            <div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 mb-3"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`transition-transform ${showDetails ? "rotate-90" : ""}`}
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
                {t(lang, "details")}
              </button>

              {showDetails && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                  <InfoRow label="曲目 ID" value={track?.id || "-"} mono />
                  <InfoRow label="名称 (中)" value={track?.name_zh || "-"} />
                  <InfoRow label="名称 (EN)" value={track?.name_en || track?.name || "-"} />
                  <InfoRow label="类型" value={track?.type || "-"} />
                  <InfoRow label={t(lang, "category")} value={track?.category || "-"} />
                  <InfoRow label="时长" value={track?.duration_s ? formatTime(track.duration_s) : "-"} />
                  <InfoRow label="文件大小" value={formatFileSize(track?.file_size)} />
                  <InfoRow label="创建时间" value={track?.created_at || "-"} />
                  <InfoRow label="场景" value={scene?.emoji + " " + (scene?.name || "-")} />
                  <InfoRow label="场景 ID" value={scene?.id || "-"} />
                  <InfoRow label="播放次数" value={String(scene?.plays || "-")} />
                  {scene?.mood && (
                    <InfoRow
                      label={t(lang, "tags")}
                      value={scene.mood.map((m) => `#${m}`).join(" ")}
                    />
                  )}
                  {scene?.useCases && (
                    <InfoRow
                      label="适用场景"
                      value={scene.useCases.join(" / ")}
                    />
                  )}
                  <div className="col-span-2 mt-2">
                    <span className="text-white/30 text-xs block mb-1">AI 创作 Prompt</span>
                    <p className="text-white/50 text-xs leading-relaxed bg-white/5 rounded-lg p-3 font-mono">
                      {track?.prompt || "（无 prompt 信息）"}
                    </p>
                  </div>
                  {scene?.description && (
                    <div className="col-span-2 mt-1">
                      <span className="text-white/30 text-xs block mb-1">{t(lang, "description")}</span>
                      <p className="text-white/50 text-xs leading-relaxed">{scene.description}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 底部留白 */}
            <div className="h-8" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** 信息行 */
function InfoRow({ label, value, mono }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-white/30 text-xs shrink-0 min-w-[5em]">{label}</span>
      <span className={`text-white/50 text-xs ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
